/**
 * Global Download Progress Toast Controller
 * Manages active state, percentage, titles, and statuses for the custom download toast.
 */

const listeners = new Set();

let currentState = {
  active: false,
  percent: 0,
  title: "",
  status: "",
  isComplete: false,
};

export const downloadProgress = {
  /**
   * Subscribe to state updates
   */
  subscribe(listener) {
    listeners.add(listener);
    listener(currentState);
    return () => {
      listeners.delete(listener);
    };
  },

  /**
   * Get current state
   */
  getState() {
    return currentState;
  },

  /**
   * Start a download with initial percent & message
   */
  start(title = "Duty Slip PDF", status = "Connecting to database...") {
    let cleanTitle = title;
    if (!cleanTitle.startsWith("Downloading") && !cleanTitle.startsWith("Downloaded")) {
      cleanTitle = `Downloading ${cleanTitle}`;
    }
    currentState = {
      active: true,
      percent: 10,
      title: cleanTitle,
      status,
      isComplete: false,
    };
    listeners.forEach((fn) => fn(currentState));
  },

  /**
   * Update progress percentage and optional status text
   */
  update(percent, status, customTitle) {
    const isDone = percent >= 100;
    let title = customTitle || currentState.title;

    if (isDone) {
      if (title.startsWith("Downloading ")) {
        title = title.replace(/^Downloading\s+/i, "Downloaded ");
      } else if (!title.startsWith("Downloaded")) {
        title = `Downloaded ${title}`;
      }
    }

    currentState = {
      ...currentState,
      percent: Math.min(100, Math.max(0, percent)),
      status: status !== undefined ? status : currentState.status,
      title,
      isComplete: isDone,
    };

    listeners.forEach((fn) => fn(currentState));

    if (isDone) {
      setTimeout(() => {
        downloadProgress.close();
      }, 2300);
    }
  },

  /**
   * Mark as 100% complete with final success message
   */
  complete(message = "Downloaded successfully!", title) {
    downloadProgress.update(100, message, title);
  },

  /**
   * Close or reset the toast immediately
   */
  close() {
    currentState = {
      active: false,
      percent: 0,
      title: "",
      status: "",
      isComplete: false,
    };
    listeners.forEach((fn) => fn(currentState));
  },

  /**
   * Handle error
   */
  error(status = "Failed to download document") {
    downloadProgress.close();
  },
};

export default downloadProgress;
